import { CONSTANTS } from '../../constants/constants';
import { encodeParam } from '../../services/params.utils';
import { useLocation, useNavigate } from 'react-router-dom';
import { removeTabAndSubTabFromFilePath } from '../../services/urlInterceptor.utils';
import { getActivityVerb, getFallbackHeaderLabel } from './activityFeedHeaderShared';
import VulnerabilityUpdatedHeader from './activityFeedHeaders/VulnerabilityUpdatedHeader';
import VulnerabilityEntityHeader from './activityFeedHeaders/VulnerabilityEntityHeader';
import VulnerabilityRemediatedHeader from './activityFeedHeaders/VulnerabilityRemediatedHeader';
import MisconfigChangedHeader from './activityFeedHeaders/MisconfigChangedHeader';
import IdentificationHeader from './activityFeedHeaders/IdentificationHeader';
import AssetGroupHeader from './activityFeedHeaders/AssetGroupHeader';
import GroupHeader from './activityFeedHeaders/GroupHeader';
import GroupMembershipChangedHeader from './activityFeedHeaders/GroupMembershipChangedHeader';
import OrgSettingsUpdatedHeader from './activityFeedHeaders/OrgSettingsUpdatedHeader';
import AssetAnalysisCompletedHeader from './activityFeedHeaders/AssetAnalysisCompletedHeader';
import AssetRiskChangedHeader from './activityFeedHeaders/AssetRiskChangedHeader';
import SecretRemediatedHeader from './activityFeedHeaders/SecretRemediatedHeader';
import LicenseIssueRemediatedHeader from './activityFeedHeaders/LicenseIssueRemediatedHeader';
import DefaultActivityHeader from './activityFeedHeaders/DefaultActivityHeader';

export { getActivityFeedSecondaryLineCount } from './activityFeedHeaderShared';

const { CREDENTIALS, LICENSE_ISSUES, SBOM, VULNERABILITIES, MISCONFIGURATIONS } = CONSTANTS.REPORT.TABS;
const { EQUAL } = CONSTANTS.FILTER_OPERATIONS;
const { EXPOSED_SECRETS } = CONSTANTS.REPORT.CREDENTIALS_SUB_TABS;

function NRActivityFeedItemHeader({ item, handleClose }) {
  const { entity } = item;
  const location = useLocation();
  const activity = getActivityVerb(item);
  const navigate = useNavigate();

  const handleClick = () => {
    navigate(handleTableRedirect(entity, entity.__typename));
    handleClose();
  };

  const getEntityLabel = () => {
    return (
      entity?.cve ||
      entity?.checkName ||
      entity?.name ||
      entity?.componentName ||
      entity?.issueName ||
      entity?.userName ||
      entity?.hash ||
      entity?.checkId ||
      entity?.groupId ||
      getFallbackHeaderLabel(item)
    );
  };

  const handleLinkActivate = event => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleClick();
    }
  };

  const interactiveLinkProps = {
    role: 'link',
    tabIndex: 0,
    onClick: handleClick,
    onKeyDown: handleLinkActivate,
    'aria-label': `${getEntityLabel()} ${activity}`
  };

  const handleTableRedirect = (entity, typename) => {
    let redirectToBaseURL = removeTabAndSubTabFromFilePath(location);
    let extraParams = new URLSearchParams();
    let tab = '';
    let subTab = '';
    switch (typename) {
      case 'VulnerabilityUpdatedPayload':
      case 'VulnerabilityAddedPayload':
      case 'VulnerabilityRemovedPayload':
      case 'VulnerabilityRemediatedPayload':
        tab = VULNERABILITIES;
        extraParams.append(
          'vulnerabilityColumnFilter',
          encodeParam([
            {
              fieldName: 'CVE',
              value: entity.cve,
              operation: EQUAL
            }
          ])
        );
        break;
      case 'MisconfigChangedPayload':
        tab = MISCONFIGURATIONS;
        extraParams.append(
          'misconfigurationColumnsFilter',
          encodeParam([
            {
              fieldName: 'DISPLAYNAME',
              value: entity.checkName,
              operation: EQUAL
            }
          ])
        );
        break;
      case 'IdentificationAddedPayload':
      case 'IdentificationRemovedPayload':
        tab = SBOM;
        extraParams.append(
          'componentColumnFilter',
          encodeParam(
            [
              {
                fieldName: 'NAME',
                value: entity.name,
                operation: CONSTANTS.FILTER_OPERATIONS.EQUAL.toUpperCase()
              },
              {
                fieldName: 'VENDOR',
                value: entity.vendor,
                operation: CONSTANTS.FILTER_OPERATIONS.EQUAL.toUpperCase()
              },
              {
                fieldName: 'VERSION',
                value: entity.version,
                operation: CONSTANTS.FILTER_OPERATIONS.EQUAL.toUpperCase()
              }
            ].filter(item => item.value)
          )
        );
        break;
      case 'SecretRemediatedPayload':
        tab = CREDENTIALS;
        subTab = encodeParam(EXPOSED_SECRETS);
        extraParams.append('stringFilter', encodeParam(entity.filePath));
        break;
      case 'LicenseIssueRemediatedPayload':
        tab = LICENSE_ISSUES;
        extraParams.append(
          'licenseIssuesColumnFilter',
          encodeParam(
            [
              {
                fieldName: 'COMPONENTNAME',
                value: entity.componentName,
                operation: CONSTANTS.FILTER_OPERATIONS.EQUAL.toUpperCase()
              },
              {
                fieldName: 'ISSUENAME',
                value: entity.issueName,
                operation: CONSTANTS.FILTER_OPERATIONS.EQUAL.toUpperCase()
              }
            ].filter(item => item.value)
          )
        );
        break;
      default:
        break;
    }
    return `${redirectToBaseURL}${!!tab ? '/' + tab : ''}${!!subTab ? '/' + subTab : ''}?${extraParams.toString()}`;
  };

  const sharedProps = { entity, item, activity, linkProps: interactiveLinkProps };

  switch (entity.__typename) {
    case 'VulnerabilityUpdatedPayload':
      return <VulnerabilityUpdatedHeader {...sharedProps} />;
    case 'VulnerabilityRemediatedPayload':
      return <VulnerabilityRemediatedHeader {...sharedProps} />;
    case 'VulnerabilityAddedPayload':
    case 'VulnerabilityRemovedPayload':
      return <VulnerabilityEntityHeader {...sharedProps} />;
    case 'MisconfigChangedPayload':
      return <MisconfigChangedHeader {...sharedProps} />;
    case 'IdentificationAddedPayload':
    case 'IdentificationRemovedPayload':
      return <IdentificationHeader {...sharedProps} />;
    case 'AssetGroupAddedPayload':
    case 'AssetGroupRemovedPayload':
      return <AssetGroupHeader {...sharedProps} />;
    case 'GroupCreatedPayload':
    case 'GroupUpdatedPayload':
    case 'GroupDeletedPayload':
      return <GroupHeader {...sharedProps} />;
    case 'GroupMembershipChangedPayload':
      return <GroupMembershipChangedHeader {...sharedProps} />;
    case 'OrgSettingsUpdatedPayload':
      return <OrgSettingsUpdatedHeader {...sharedProps} />;
    case 'AssetAnalysisCompletedPayload':
      return <AssetAnalysisCompletedHeader {...sharedProps} />;
    case 'AssetRiskChangedPayload':
      return <AssetRiskChangedHeader {...sharedProps} />;
    case 'SecretRemediatedPayload':
      return <SecretRemediatedHeader {...sharedProps} />;
    case 'LicenseIssueRemediatedPayload':
      return <LicenseIssueRemediatedHeader {...sharedProps} />;
    default:
      return <DefaultActivityHeader {...sharedProps} />;
  }
}

export default NRActivityFeedItemHeader;
